import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  IconPlus, IconChevronLeft, IconChevronRight, IconPencil, IconTrash,
  IconPaperclip, IconFile, IconRefresh, IconBell, IconLayoutList,
  IconLayoutGrid, IconNews, IconMessageCircle, IconSend, IconPhoto,
} from '@tabler/icons-react';
import JoditEditor from 'jodit-react';
import DOMPurify from 'dompurify';
import api from '../api/api';
import SummernoteEditor from '../components/SummernoteEditor';

/** 파일 크기 포맷 */
const formatSize = (bytes) => {
  if (!bytes) return '0B';
  if (bytes < 1024) return bytes + 'B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
};

/** HTML 렌더링 (base64 이미지 허용) */
const PURIFY_CONFIG = { ADD_DATA_URI_TAGS: ['img'] };
const renderContent = (content) => {
  if (!content) return '';
  const t = content.trim();
  if (t.startsWith('<')) return DOMPurify.sanitize(t, PURIFY_CONFIG);
  return DOMPurify.sanitize('<p>' + t.replace(/\n/g, '<br>') + '</p>', PURIFY_CONFIG);
};

/** HTML을 텍스트만 추출 */
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

/**
 * 인증이 필요한 파일을 Axios로 가져와 blob URL로 표시하는 썸네일 컴포넌트
 * (파일 첨부 이미지 갤러리 썸네일용)
 */
const AuthImageThumbnail = ({ fileId, divStyle, fallback }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!fileId) return;
    let objectUrl = null;
    api.get(`/files/${fileId}`, { responseType: 'blob' })
      .then(res => {
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [fileId]);
  if (!blobUrl) return fallback || null;
  return (
    <div style={{
      ...divStyle,
      backgroundImage: `url(${blobUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }} />
  );
};

/** Jodit 에디터 내부 */
const JoditRichEditor = ({ onChange, initialValue = '', placeholder = '내용을 입력하세요' }) => {
  const config = useMemo(() => ({
    language: 'ko',
    placeholder,
    height: 400,
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'brush', 'font', 'fontsize', '|',
      'ul', 'ol', '|',
      'paragraph', '|',
      'hr', 'image', '|',
      'undo', 'redo', '|',
      'source',
    ],
    toolbarAdaptive: false,
    showXPathInStatusbar: false,
    showCharsCounter: false,
    showWordsCounter: false,
    askBeforePasteHTML: false,
    defaultActionOnPaste: 'insert_as_html',
    style: {
      font: '14px Arial, sans-serif',
      color: '#1a1a1a',
      background: '#ffffff',
    },
    uploader: {
      insertImageAsBase64URI: true,
      imagesExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'],
    },
    imageProcessor: {
      replaceDataURIToBlobIdInView: false,
    },
    controls: {
      fontsize: {
        list: ['9', '10', '11', '12', '13', '14', '16', '18', '20', '24', '28', '32', '36', '48'],
      },
      font: {
        list: {
          '': '기본',
          "'Malgun Gothic', '맑은 고딕', sans-serif": '맑은 고딕',
          "'Nanum Gothic', '나눔고딕', sans-serif": '나눔고딕',
          "'Nanum Myeongjo', '나눔명조', serif": '나눔명조',
          "'Dotum', '돋움', sans-serif": '돋움',
          'Arial, sans-serif': 'Arial',
          "'Times New Roman', Times, serif": 'Times New Roman',
        },
      },
    },
  }), []);

  return <JoditEditor config={config} value={initialValue} onChange={onChange} />;
};

/** 에디터 설정에 따라 Jodit 또는 Summernote 렌더 (모바일: textarea) */
const isMobile = () => window.innerWidth <= 768;

const RichEditor = ({ onChange, initialValue = '', placeholder = '내용을 입력하세요', editorType = 'jodit' }) => {
  if (isMobile()) {
    return (
      <textarea
        className="form-control form-control-sm"
        rows={10}
        placeholder={placeholder}
        defaultValue={initialValue}
        onChange={e => onChange(e.target.value)}
        style={{ fontSize: 14 }}
      />
    );
  }
  if (editorType === 'summernote') {
    return <SummernoteEditor onChange={onChange} initialValue={initialValue} placeholder={placeholder} />;
  }
  return <JoditRichEditor onChange={onChange} initialValue={initialValue} placeholder={placeholder} />;
};

const fmt = (v) => v ? v.replace('T', ' ').slice(0, 16) : '-';

export default function BoardPage({ board, me, showToast, initialPostId, editorType = 'jodit' }) {
  const [view, setView]           = useState('list'); // 'list' | 'detail' | 'write' | 'edit'
  const [posts, setPosts]         = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [page, setPage]           = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading]     = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving]       = useState(false);

  /* 글쓰기/수정 폼 */
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formIsNotice, setFormIsNotice] = useState(false);
  const [formIsPopup, setFormIsPopup]   = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  /* 파일 첨부 */
  const [attachFiles, setAttachFiles] = useState([]);
  const fileInputRef = useRef(null);

  /* 답글 */
  const [replies, setReplies]         = useState([]);
  const [replyText, setReplyText]     = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [loadingReplies, setLoadingReplies] = useState(false);

  /* 댓글 */
  const [comments, setComments]             = useState([]);
  const [commentText, setCommentText]       = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [loadingComments, setLoadingComments]     = useState(false);

  const isAdminOrMaster = me?.role === 'ADMIN' || me?.role === 'MASTER';
  const pageSize = board.pageSize || 20;

  /* ── 목록 조회 ── */
  const fetchPosts = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const res = await api.get('/posts', { params: { boardId: board.id, page: p, size: pageSize } });
      const content = res.data.content;
      setPosts(content);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);
      setPage(p);

    } catch {
      showToast('게시글 목록 조회 실패', 'danger');
    } finally {
      setLoading(false);
    }
  }, [board.id, pageSize, showToast]);

  useEffect(() => {
    setView('list');
    setSelectedPost(null);
    if (initialPostId) {
      // 팝업에서 진입: 바로 해당 게시글 상세 열기
      setLoadingDetail(true);
      setView('detail');
      api.get(`/posts/${initialPostId}`).then(res => {
        setSelectedPost(res.data);
        if (board.allowReply) fetchRepliesDirect(initialPostId);
        if (board.allowComment) fetchComments(initialPostId);
      }).catch(() => setView('list')).finally(() => setLoadingDetail(false));
    }
    fetchPosts(0);
  }, [board.id]);

  /* ── 답글 목록 조회 ── */
  const fetchReplies = useCallback(async (postId) => {
    setLoadingReplies(true);
    try {
      const res = await api.get('/posts', { params: { boardId: board.id, page: 0, size: 100, parentId: postId } });
      // parentId 필터는 백엔드에서 처리 안됨 → 직접 필터
      // 대신 postId에 대한 답글을 posts 목록에서 parentId로 필터
      // 실제로는 /posts?boardId=&parentId= 지원이 필요하지만 간단히 처리
      // 여기서는 별도 API 없이 전체 목록에서 필터하거나
      // 또는 단순 fetch로 처리
      setReplies(res.data.content.filter(p => p.parentId === postId));
    } catch {
      setReplies([]);
    } finally {
      setLoadingReplies(false);
    }
  }, [board.id]);

  /* ── 상세 조회 ── */
  const handleSelectPost = async (post) => {
    setLoadingDetail(true);
    setView('detail');
    setReplyText('');
    setReplies([]);
    setCommentText('');
    setComments([]);
    try {
      const res = await api.get(`/posts/${post.id}`);
      setSelectedPost(res.data);
      setPosts(prev => prev.map(p => p.id === post.id
        ? { ...p, viewCount: (p.viewCount || 0) + 1 } : p));
      if (board.allowReply) {
        loadRepliesForPost(post.id);
      }
      if (board.allowComment) {
        fetchComments(post.id);
      }
    } catch {
      showToast('게시글 조회 실패', 'danger');
      setView('list');
    } finally {
      setLoadingDetail(false);
    }
  };

  const loadRepliesForPost = async (postId) => {
    setLoadingReplies(true);
    try {
      // GET /posts?boardId=&page=0&size=100 후 parentId 필터
      const res = await api.get('/posts', {
        params: { boardId: board.id, page: 0, size: 100 },
      });
      // 백엔드 목록은 parentId=null 원글만 반환하므로 실제 답글은 별도로 가져와야 함
      // 여기서는 selectedPost의 replyCount를 이용하되, 실제 답글은 아래 방식으로 조회
      // 간단하게 posts의 전체를 받아서 parentId 필터 → 안됨(원글만 나오므로)
      // → 답글 전용 엔드포인트 없으므로 임시로 빈 배열
      setReplies([]);
      // 실제로는 /posts/by-parent/{postId} 형태로 가져와야 하지만
      // 현재 백엔드에 해당 엔드포인트가 없으므로 별도 추가 필요
      // 여기서는 아래 fetchRepliesDirect로 처리
      await fetchRepliesDirect(postId);
    } catch {
      setReplies([]);
    } finally {
      setLoadingReplies(false);
    }
  };

  /* 답글 직접 조회 (parentId 기반) */
  const fetchRepliesDirect = async (postId) => {
    try {
      // 백엔드에 /posts?boardId=&parentId= 파라미터 지원 필요
      // 현재는 지원하지 않으므로 클라이언트에서 빈 배열로 시작 후 작성 시 추가
      // 임시로 빈 배열 유지 (추후 백엔드 엔드포인트 추가 시 사용)
      const res = await api.get(`/posts/replies/${postId}`).catch(() => ({ data: [] }));
      setReplies(Array.isArray(res.data) ? res.data : []);
    } catch {
      setReplies([]);
    }
  };

  /* ── 글쓰기 모드 진입 ── */
  const handleWrite = () => {
    setFormTitle('');
    setFormContent('');
    setFormIsNotice(false);
    setFormIsPopup(false);
    setAttachFiles([]);
    setEditorKey(k => k + 1);
    setView('write');
  };

  /* ── 수정 모드 진입 ── */
  const handleEdit = () => {
    setFormTitle(selectedPost.title);
    setFormContent(selectedPost.content || '');
    setFormIsNotice(selectedPost.isNotice || false);
    setFormIsPopup(selectedPost.isPopup || false);
    setAttachFiles([]);
    setEditorKey(k => k + 1);
    setView('edit');
  };

  /* ── 저장 (작성/수정 공통) ── */
  const handleSave = async () => {
    if (!formTitle.trim()) { showToast('제목을 입력하세요.', 'warning'); return; }
    if (!formContent || !formContent.replace(/<[^>]*>/g, '').trim()) {
      showToast('내용을 입력하세요.', 'warning'); return;
    }
    if (attachFiles.some(f => f.uploading)) {
      showToast('파일 업로드가 완료될 때까지 기다려주세요.', 'warning'); return;
    }
    setSaving(true);
    try {
      const attachmentIds = attachFiles.filter(f => f.id).map(f => f.id);
      if (view === 'write') {
        const res = await api.post('/posts', {
          boardId: board.id,
          title: formTitle.trim(),
          content: formContent,
          isNotice: formIsNotice,
          isPopup: formIsPopup,
          attachmentIds,
        });
        showToast('게시글이 등록되었습니다.');
        await fetchPosts(0);
        const detailRes = await api.get(`/posts/${res.data.id}`);
        setSelectedPost(detailRes.data);
        setReplies([]);
        setView('detail');
      } else {
        const res = await api.put(`/posts/${selectedPost.id}`, {
          boardId: board.id,
          title: formTitle.trim(),
          content: formContent,
          isNotice: formIsNotice,
          isPopup: formIsPopup,
          attachmentIds,
        });
        showToast('수정되었습니다.');
        setSelectedPost(res.data);
        setView('detail');
        await fetchPosts(page);
      }
    } catch {
      showToast('저장 실패', 'danger');
    } finally {
      setSaving(false);
    }
  };

  /* ── 삭제 ── */
  const handleDelete = async () => {
    if (!window.confirm('이 게시글을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/posts/${selectedPost.id}`);
      showToast('삭제되었습니다.');
      setSelectedPost(null);
      setView('list');
      await fetchPosts(page);
    } catch {
      showToast('삭제 실패', 'danger');
    }
  };

  /* ── 파일 선택 → 즉시 업로드 ── */
  const handleFileSelect = async (e) => {
    const selected = Array.from(e.target.files);
    e.target.value = '';
    for (const file of selected) {
      if (file.size > 10 * 1024 * 1024) {
        showToast(`"${file.name}" 파일이 10MB를 초과합니다.`, 'warning');
        continue;
      }
      const tempId = Date.now() + Math.random();
      setAttachFiles(prev => [...prev, { tempId, name: file.name, size: file.size, uploading: true }]);
      try {
        const formData = new FormData();
        formData.append('files', file);
        const res = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const uploaded = res.data[0];
        setAttachFiles(prev => prev.map(f =>
          f.tempId === tempId ? { ...f, id: uploaded.id, uploading: false } : f
        ));
      } catch {
        setAttachFiles(prev => prev.map(f =>
          f.tempId === tempId ? { ...f, uploading: false, error: '업로드 실패' } : f
        ));
        showToast(`"${file.name}" 업로드에 실패했습니다.`, 'danger');
      }
    }
  };

  const removeAttach = async (tempId) => {
    const target = attachFiles.find(f => f.tempId === tempId);
    if (target?.id) {
      try { await api.delete(`/files/${target.id}`); } catch { /* 무시 */ }
    }
    setAttachFiles(prev => prev.filter(f => f.tempId !== tempId));
  };

  /* ── 파일 다운로드 ── */
  const handleDownload = async (fileId, fileName) => {
    try {
      const res = await api.get(`/files/${fileId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showToast('파일 다운로드 실패', 'danger');
    }
  };

  /* ── 답글 작성 ── */
  const handleSubmitReply = async () => {
    if (!replyText.trim()) { showToast('답글 내용을 입력하세요.', 'warning'); return; }
    setSubmittingReply(true);
    try {
      const res = await api.post('/posts', {
        boardId: board.id,
        title: `Re: ${selectedPost.title}`,
        content: replyText.trim(),
        isNotice: false,
        parentId: selectedPost.id,
      });
      showToast('답글이 등록되었습니다.');
      setReplyText('');
      // 새 답글 바로 추가
      const newReply = {
        id: res.data.id,
        title: res.data.title,
        content: res.data.content,
        authorName: res.data.authorName,
        authorId: res.data.authorId,
        createdAt: res.data.createdAt,
        parentId: selectedPost.id,
      };
      setReplies(prev => [...prev, newReply]);
      // 목록의 replyCount 업데이트
      setPosts(prev => prev.map(p => p.id === selectedPost.id
        ? { ...p, replyCount: (p.replyCount || 0) + 1 } : p));
      setSelectedPost(prev => prev ? { ...prev, replyCount: (prev.replyCount || 0) + 1 } : prev);
    } catch {
      showToast('답글 등록 실패', 'danger');
    } finally {
      setSubmittingReply(false);
    }
  };

  /* ── 답글 삭제 ── */
  const handleDeleteReply = async (replyId) => {
    if (!window.confirm('답글을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/posts/${replyId}`);
      showToast('삭제되었습니다.');
      setReplies(prev => prev.filter(r => r.id !== replyId));
      setSelectedPost(prev => prev ? { ...prev, replyCount: Math.max(0, (prev.replyCount || 0) - 1) } : prev);
    } catch {
      showToast('삭제 실패', 'danger');
    }
  };

  /* ── 댓글 목록 조회 ── */
  const fetchComments = useCallback(async (postId) => {
    setLoadingComments(true);
    try {
      const res = await api.get('/comments', { params: { postId } });
      setComments(Array.isArray(res.data) ? res.data : []);
    } catch {
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, []);

  /* ── 댓글 작성 ── */
  const handleSubmitComment = async () => {
    if (!commentText.trim()) { showToast('댓글 내용을 입력하세요.', 'warning'); return; }
    if (commentText.length > 1000) { showToast('댓글은 1000자 이하로 입력하세요.', 'warning'); return; }
    setSubmittingComment(true);
    try {
      const res = await api.post('/comments', {
        postId: selectedPost.id,
        content: commentText.trim(),
      });
      setComments(prev => [...prev, res.data]);
      setCommentText('');
    } catch {
      showToast('댓글 등록 실패', 'danger');
    } finally {
      setSubmittingComment(false);
    }
  };

  /* ── 댓글 삭제 ── */
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (err) {
      showToast(err.response?.data?.error || '삭제 실패', 'danger');
    }
  };

  /* 댓글 삭제 버튼 노출 여부 */
  const canDeleteComment = (comment) => {
    if (me?.role === 'MASTER') return true;
    if (me?.role === 'ADMIN' && (comment.authorId === me?.id || comment.authorRole === 'USER')) return true;
    return comment.authorId === me?.id;
  };

  /* 수정/삭제 공통 권한 체크:
     MASTER → 모든 글
     ADMIN  → 본인 글 + USER 작성 글
     USER   → 본인 글만 */
  const canEditOrDelete = selectedPost && (() => {
    if (me?.role === 'MASTER') return true;
    if (me?.role === 'ADMIN') {
      return selectedPost.authorId === me?.id || selectedPost.authorRole === 'USER';
    }
    return selectedPost.authorId === me?.id;
  })();

  const canEdit = canEditOrDelete;
  const canDelete = canEditOrDelete;

  /* ── 목록 타입별 렌더링 ── */
  const listType = board.listType || 'LIST';

  const renderListView = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" style={{ width: 36, height: 36 }} />
        </div>
      );
    }
    if (posts.length === 0) {
      return (
        <div className="text-center text-muted py-5">
          <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
          <div>게시글이 없습니다.</div>
          <button className="btn btn-primary btn-sm mt-3 d-flex align-items-center gap-1 mx-auto"
            onClick={handleWrite}>
            <IconPlus size={14} /> 첫 글 작성
          </button>
        </div>
      );
    }

    if (listType === 'GALLERY') {
      return (
        <div className="p-3">
          <div className="row g-3">
            {posts.map(post => (
              <div key={post.id} className="col-sm-6 col-md-4 col-lg-3">
                <div className="card card-sm h-100"
                  style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.12)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
                  onClick={() => handleSelectPost(post)}>
                  {/* 썸네일 영역 */}
                  {(() => {
                    const thumbStyle = {
                      height: 140, overflow: 'hidden',
                      borderRadius: '4px 4px 0 0',
                    };
                    const fallback = (
                      <div style={{
                        ...thumbStyle,
                        background: post.isNotice ? '#eef2ff' : '#f4f6fb',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 40,
                      }}>
                        {post.isNotice ? '📢' : '📄'}
                      </div>
                    );
                    // ① 본문 이미지 base64 (리사이즈됨)
                    if (post.thumbnail) {
                      return (
                        <div style={{
                          ...thumbStyle,
                          backgroundImage: `url(${post.thumbnail})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }} />
                      );
                    }
                    // ② 첨부 이미지 파일 (인증 fetch)
                    if (post.thumbnailFileId) {
                      return (
                        <AuthImageThumbnail
                          fileId={post.thumbnailFileId}
                          divStyle={thumbStyle}
                          fallback={fallback}
                        />
                      );
                    }
                    // ③ 이미지 없음 → 이모지
                    return fallback;
                  })()}
                  <div className="card-body py-2 px-3">
                    {post.isNotice && (
                      <span className="badge bg-danger text-white me-1" style={{ fontSize: 10 }}>공지</span>
                    )}
                    <div className="fw-semibold text-truncate" style={{ fontSize: 13 }}
                      title={post.title}>
                      {post.title}
                    </div>
                    <div className="text-muted d-flex align-items-center justify-content-between mt-1"
                      style={{ fontSize: 11 }}>
                      <span className="d-flex align-items-center gap-1">
                        {post.authorProfileImageThumb && (
                          <img src={post.authorProfileImageThumb} alt="" style={{
                            width: 16, height: 16, borderRadius: '50%', objectFit: 'cover',
                          }} />
                        )}
                        {post.authorName}
                      </span>
                      <span className="d-flex align-items-center gap-2">
                        {post.attachments?.length > 0 && <IconPaperclip size={11} />}
                        {board.allowReply && post.replyCount > 0 && (
                          <span className="d-flex align-items-center gap-1">
                            <IconMessageCircle size={11} />{post.replyCount}
                          </span>
                        )}
                        <span>조회 {post.viewCount}</span>
                      </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: 10 }}>
                      {fmt(post.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (listType === 'BLOG') {
      return (
        <div className="p-3">
          {posts.map(post => {
            const preview = stripHtml(post.content || '').slice(0, 160);
            return (
              <div key={post.id}
                className="p-3 mb-3 border rounded"
                style={{ cursor: 'pointer', background: post.isNotice ? 'rgba(32,107,196,0.03)' : '#fff', transition: 'box-shadow 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
                onClick={() => handleSelectPost(post)}>
                <div className="d-flex align-items-center gap-2 mb-1">
                  {post.isNotice && <span className="badge bg-danger text-white" style={{ fontSize: 10 }}>공지</span>}
                  <span className="fw-semibold" style={{ fontSize: 15 }}>{post.title}</span>
                  {post.attachments?.length > 0 && <IconPaperclip size={13} className="text-muted" />}
                  {board.allowReply && post.replyCount > 0 && (
                    <span className="badge bg-azure-lt text-azure d-flex align-items-center gap-1" style={{ fontSize: 10 }}>
                      <IconMessageCircle size={11} />{post.replyCount}
                    </span>
                  )}
                </div>
                {preview && (
                  <div className="text-muted" style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 8 }}>
                    {preview}{preview.length === 160 ? '...' : ''}
                  </div>
                )}
                <div className="text-muted d-flex align-items-center gap-3" style={{ fontSize: 12 }}>
                  <span className="d-flex align-items-center gap-1">
                    {post.authorProfileImageThumb && (
                      <img src={post.authorProfileImageThumb} alt="" style={{
                        width: 18, height: 18, borderRadius: '50%', objectFit: 'cover',
                      }} />
                    )}
                    {post.authorName}
                  </span>
                  <span>{fmt(post.createdAt)}</span>
                  <span>조회 {post.viewCount}</span>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // LIST (기본 테이블형)
    return (
      <div className="table-responsive">
        <table className="table table-vcenter card-table table-hover">
          <thead>
            <tr>
              <th style={{ width: 50 }}>번호</th>
              <th>제목</th>
              <th style={{ width: 100 }}>작성자</th>
              <th style={{ width: 130 }}>날짜</th>
              <th style={{ width: 70, textAlign: 'center' }}>조회</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post, idx) => (
              <tr key={post.id}
                style={{
                  cursor: 'pointer',
                  background: post.isNotice ? 'rgba(32,107,196,0.04)' : undefined,
                }}
                onClick={() => handleSelectPost(post)}>
                <td className="text-muted" style={{ fontSize: 13 }}>
                  {post.isNotice
                    ? <span className="badge bg-danger-lt text-danger" style={{ fontSize: 10 }}>공지</span>
                    : totalElements - (page * pageSize + idx)}
                </td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    {post.isNotice && <IconBell size={13} className="text-danger flex-shrink-0" />}
                    {post.isPopup && <IconPhoto size={13} className="text-primary flex-shrink-0" />}
                    <span className={post.isNotice ? 'fw-semibold' : ''} style={{ fontSize: 14 }}>
                      {post.title}
                    </span>
                    {post.attachments?.length > 0 && (
                      <IconPaperclip size={12} className="text-muted flex-shrink-0" />
                    )}
                    {board.allowReply && post.replyCount > 0 && (
                      <span className="text-azure d-flex align-items-center gap-1 flex-shrink-0"
                        style={{ fontSize: 12 }}>
                        <IconMessageCircle size={12} />{post.replyCount}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-muted" style={{ fontSize: 13 }}>
                  <span className="d-flex align-items-center gap-1">
                    {post.authorProfileImageThumb && (
                      <img src={post.authorProfileImageThumb} alt="" style={{
                        width: 20, height: 20, borderRadius: '50%', objectFit: 'cover',
                      }} />
                    )}
                    {post.authorName}
                  </span>
                </td>
                <td className="text-muted" style={{ fontSize: 12 }}>{fmt(post.createdAt)}</td>
                <td className="text-center text-muted" style={{ fontSize: 12 }}>{post.viewCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  /* ── 페이지네이션 ── */
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const maxPages = 10;
    const start = Math.max(0, Math.min(page - Math.floor(maxPages / 2), totalPages - maxPages));
    const end = Math.min(totalPages, start + maxPages);
    return (
      <div className="d-flex justify-content-center align-items-center gap-1 py-3">
        <button className="btn btn-sm btn-ghost-secondary"
          disabled={page === 0} onClick={() => fetchPosts(0)}>«</button>
        <button className="btn btn-sm btn-ghost-secondary"
          disabled={page === 0} onClick={() => fetchPosts(page - 1)}>
          <IconChevronLeft size={14} />
        </button>
        {[...Array(end - start)].map((_, i) => {
          const p = start + i;
          return (
            <button key={p}
              className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-ghost-secondary'}`}
              onClick={() => fetchPosts(p)}>
              {p + 1}
            </button>
          );
        })}
        <button className="btn btn-sm btn-ghost-secondary"
          disabled={page === totalPages - 1} onClick={() => fetchPosts(page + 1)}>
          <IconChevronRight size={14} />
        </button>
        <button className="btn btn-sm btn-ghost-secondary"
          disabled={page === totalPages - 1} onClick={() => fetchPosts(totalPages - 1)}>»</button>
      </div>
    );
  };

  return (
    <div className="page-body">
      <div className="container-xl">

        {/* ── 헤더 ── */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            {(view === 'detail' || view === 'write' || view === 'edit') && (
              <button className="btn btn-sm btn-ghost-secondary p-1"
                onClick={() => { setView('list'); setSelectedPost(null); }}>
                <IconChevronLeft size={18} />
              </button>
            )}
            <div>
              <div className="text-muted small d-flex align-items-center gap-2">
                게시판
                {/* 목록 형태 아이콘 표시 */}
                {view === 'list' && (
                  <span className="text-muted" style={{ fontSize: 11 }}>
                    {listType === 'GALLERY' && <><IconLayoutGrid size={12} className="me-1" />갤러리형</>}
                    {listType === 'BLOG' && <><IconNews size={12} className="me-1" />블로그형</>}
                    {listType === 'LIST' && <><IconLayoutList size={12} className="me-1" />리스트형</>}
                    {' '}· {pageSize}개/페이지
                  </span>
                )}
              </div>
              <h2 className="mb-0" style={{ fontSize: 20, fontWeight: 700 }}>{board.name}</h2>
            </div>
          </div>
          <div className="d-flex gap-2">
            {view === 'list' && (
              <>
                <button className="btn btn-sm btn-ghost-secondary d-flex align-items-center gap-1"
                  onClick={() => fetchPosts(page)}>
                  <IconRefresh size={15} />
                </button>
                <button className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                  onClick={handleWrite}>
                  <IconPlus size={15} /> 글쓰기
                </button>
              </>
            )}
            {view === 'detail' && (
              <>
                {canEdit && (
                  <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                    onClick={handleEdit}>
                    <IconPencil size={14} /> 수정
                  </button>
                )}
                {canDelete && (
                  <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                    onClick={handleDelete}>
                    <IconTrash size={14} /> 삭제
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── 목록 뷰 ── */}
        {view === 'list' && (
          <div className="card">
            {renderListView()}
            {renderPagination()}
          </div>
        )}

        {/* ── 상세 뷰 ── */}
        {view === 'detail' && (
          <div>
            <div className="card mb-3">
              {loadingDetail ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : selectedPost && (
                <>
                  <div className="card-header">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      {selectedPost.isNotice && (
                        <span className="badge bg-danger text-white" style={{ fontSize: 11 }}>공지</span>
                      )}
                      <h3 className="card-title mb-0" style={{ fontSize: 18 }}>{selectedPost.title}</h3>
                    </div>
                    <div className="text-muted small d-flex gap-3 mt-1">
                      <span><strong>작성자:</strong> {selectedPost.authorName}</span>
                      <span><strong>등록일:</strong> {fmt(selectedPost.createdAt)}</span>
                      {selectedPost.updatedAt && (
                        <span><strong>수정일:</strong> {fmt(selectedPost.updatedAt)}</span>
                      )}
                      <span><strong>조회:</strong> {selectedPost.viewCount}</span>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="message-content"
                      style={{ lineHeight: 1.8, fontSize: 15, minHeight: 200 }}
                      dangerouslySetInnerHTML={{ __html: renderContent(selectedPost.content) }}
                    />

                    {/* 첨부파일 */}
                    {selectedPost.attachments?.length > 0 && (
                      <div className="mt-4 pt-3 border-top">
                        <div className="fw-semibold d-flex align-items-center gap-1 text-muted mb-2"
                          style={{ fontSize: 13 }}>
                          <IconPaperclip size={14} />
                          첨부파일 {selectedPost.attachments.length}개
                        </div>
                        {selectedPost.attachments.map(f => (
                          <button key={f.id} type="button"
                            className="d-flex align-items-center gap-2 mb-1 p-2 border rounded w-100"
                            style={{ background: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f4f6fb'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}
                            onClick={() => handleDownload(f.id, f.originalName)}>
                            <IconFile size={16} className="text-muted flex-shrink-0" />
                            <span className="flex-fill text-truncate" style={{ fontSize: 13 }}>
                              {f.originalName}
                            </span>
                            <small className="text-muted flex-shrink-0">{formatSize(f.fileSize)}</small>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="card-footer d-flex gap-2">
                    <button className="btn btn-sm btn-ghost-secondary d-flex align-items-center gap-1"
                      onClick={() => { setView('list'); setSelectedPost(null); }}>
                      <IconChevronLeft size={14} /> 목록
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* ── 답글 섹션 (allowReply 활성화된 게시판만) ── */}
            {board.allowReply && selectedPost && (
              <div className="card">
                <div className="card-header d-flex align-items-center gap-2">
                  <IconMessageCircle size={16} className="text-muted" />
                  <h5 className="card-title mb-0" style={{ fontSize: 15 }}>
                    답글 {selectedPost.replyCount > 0 ? `(${selectedPost.replyCount})` : ''}
                  </h5>
                </div>
                <div className="card-body p-0">
                  {loadingReplies ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-secondary" />
                    </div>
                  ) : replies.length === 0 ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                      아직 답글이 없습니다.
                    </div>
                  ) : (
                    replies.map(reply => (
                      <div key={reply.id} className="d-flex gap-3 px-3 py-3 border-bottom"
                        style={{ background: '#fafbfc' }}>
                        <div className="flex-fill">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="fw-semibold" style={{ fontSize: 13 }}>{reply.authorName}</span>
                            <span className="text-muted" style={{ fontSize: 11 }}>{fmt(reply.createdAt)}</span>
                          </div>
                          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {stripHtml(reply.content || '')}
                          </div>
                        </div>
                        {(isAdminOrMaster || reply.authorId === me?.id) && (
                          <button className="btn btn-sm btn-ghost-danger flex-shrink-0"
                            style={{ alignSelf: 'flex-start' }}
                            onClick={() => handleDeleteReply(reply.id)}>
                            <IconTrash size={13} />
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* 답글 작성 (ADMIN/MASTER만) */}
                {isAdminOrMaster ? (
                  <div className="card-footer">
                    <div className="d-flex gap-2">
                      <textarea
                        className="form-control form-control-sm"
                        rows={3}
                        placeholder="답글을 입력하세요..."
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && e.ctrlKey) handleSubmitReply();
                        }}
                        style={{ resize: 'none', fontSize: 14 }}
                      />
                      <button className="btn btn-primary btn-sm flex-shrink-0 d-flex align-items-center gap-1"
                        onClick={handleSubmitReply}
                        disabled={submittingReply || !replyText.trim()}
                        style={{ alignSelf: 'flex-end' }}>
                        {submittingReply
                          ? <span className="spinner-border spinner-border-sm" />
                          : <IconSend size={15} />}
                        등록
                      </button>
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                      Ctrl+Enter 로 빠르게 등록
                    </div>
                  </div>
                ) : (
                  <div className="card-footer text-center text-muted" style={{ fontSize: 13 }}>
                    답글은 관리자만 작성할 수 있습니다.
                  </div>
                )}
              </div>
            )}

            {/* ── 댓글 섹션 (allowComment 활성화된 게시판만) ── */}
            {board.allowComment && selectedPost && (
              <div className="card mt-3">
                <div className="card-header d-flex align-items-center gap-2">
                  <IconMessageCircle size={16} className="text-muted" />
                  <h5 className="card-title mb-0" style={{ fontSize: 15 }}>
                    댓글 {comments.length > 0 ? `(${comments.length})` : ''}
                  </h5>
                </div>
                <div className="card-body p-0">
                  {loadingComments ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-secondary" />
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                      아직 댓글이 없습니다.
                    </div>
                  ) : (
                    comments.map(comment => (
                      <div key={comment.id}
                        className="d-flex gap-3 px-3 py-3 border-bottom">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="fw-semibold" style={{ fontSize: 13 }}>{comment.authorName}</span>
                            {comment.authorRole !== 'USER' && (
                              <span className="badge bg-blue-lt text-blue" style={{ fontSize: 10 }}>
                                {comment.authorRole}
                              </span>
                            )}
                            <span className="text-muted" style={{ fontSize: 11 }}>{fmt(comment.createdAt)}</span>
                            {comment.updatedAt && (
                              <span className="text-muted" style={{ fontSize: 11 }}>(수정됨)</span>
                            )}
                          </div>
                          <div style={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                            {comment.content}
                          </div>
                        </div>
                        {canDeleteComment(comment) && (
                          <button className="btn btn-sm btn-ghost-danger flex-shrink-0"
                            style={{ alignSelf: 'flex-start' }}
                            onClick={() => handleDeleteComment(comment.id)}>
                            <IconTrash size={13} />
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* 댓글 작성 */}
                <div className="card-footer">
                  <div className="d-flex gap-2">
                    <div className="flex-fill">
                      <textarea
                        className="form-control form-control-sm"
                        rows={3}
                        placeholder="댓글을 입력하세요... (최대 1000자)"
                        value={commentText}
                        onChange={e => setCommentText(e.target.value.slice(0, 1000))}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && e.ctrlKey) handleSubmitComment();
                        }}
                        style={{ resize: 'none', fontSize: 14 }}
                      />
                      <div className="d-flex justify-content-between mt-1">
                        <span className="text-muted" style={{ fontSize: 11 }}>Ctrl+Enter 로 빠르게 등록</span>
                        <span className="text-muted" style={{ fontSize: 11 }}>
                          {commentText.length} / 1000
                        </span>
                      </div>
                    </div>
                    <button className="btn btn-primary btn-sm flex-shrink-0 d-flex align-items-center gap-1"
                      onClick={handleSubmitComment}
                      disabled={submittingComment || !commentText.trim()}
                      style={{ alignSelf: 'flex-start' }}>
                      {submittingComment
                        ? <span className="spinner-border spinner-border-sm" />
                        : <IconSend size={15} />}
                      등록
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── 글쓰기/수정 뷰 ── */}
        {(view === 'write' || view === 'edit') && (
          <div className="card">
            <div className="card-header">
              <h4 className="card-title mb-0">
                {view === 'write' ? '글쓰기' : '게시글 수정'}
              </h4>
            </div>
            <div className="card-body">

              {/* 제목 */}
              <div className="mb-3">
                <label className="form-label fw-semibold">제목 <span className="text-danger">*</span></label>
                <input type="text" className="form-control form-control-sm" placeholder="제목을 입력하세요"
                  value={formTitle} onChange={e => setFormTitle(e.target.value)} maxLength={200} />
              </div>

              {/* 공지/팝업 설정 (ADMIN/MASTER만) */}
              {isAdminOrMaster && (board.useNotice || board.usePopup) && (
                <div className="mb-3 d-flex gap-4">
                  {board.useNotice && (
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="isNoticeSwitch"
                        checked={formIsNotice} onChange={e => setFormIsNotice(e.target.checked)} />
                      <label className="form-check-label d-flex align-items-center gap-1" htmlFor="isNoticeSwitch">
                        <IconBell size={14} className="text-danger" />
                        <strong>공지글로 등록</strong>
                        <span className="text-muted small ms-1">(목록 상단 고정)</span>
                      </label>
                    </div>
                  )}
                  {board.usePopup && (
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="isPopupSwitch"
                        checked={formIsPopup} onChange={e => setFormIsPopup(e.target.checked)} />
                      <label className="form-check-label d-flex align-items-center gap-1" htmlFor="isPopupSwitch">
                        <IconPhoto size={14} className="text-primary" />
                        <strong>팝업으로 등록</strong>
                        <span className="text-muted small ms-1">(대시보드 팝업 노출)</span>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* 에디터 */}
              <div className="mb-3">
                <label className="form-label fw-semibold">내용 <span className="text-danger">*</span></label>
                <RichEditor key={`${editorKey}-${editorType}`} initialValue={formContent} onChange={setFormContent} editorType={editorType} />
              </div>

              {/* 파일 첨부 (allowAttachment) */}
              {board.allowAttachment && (
                <div className="mb-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="form-label fw-semibold mb-0" style={{ fontSize: 14 }}>
                      <IconPaperclip size={14} className="me-1" />파일 첨부
                    </span>
                    <button type="button"
                      className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={attachFiles.filter(f => f.id).length >= 5}>
                      <IconFile size={13} /> 파일 선택
                    </button>
                    <span className="text-muted small">최대 5개 · 10MB 이하</span>
                  </div>
                  <input type="file" multiple ref={fileInputRef}
                    onChange={handleFileSelect} style={{ display: 'none' }} />
                  {attachFiles.length > 0 && (
                    <div className="border rounded p-2" style={{ background: '#f8f9fa' }}>
                      {attachFiles.map(f => (
                        <div key={f.tempId}
                          className="d-flex align-items-center gap-2 py-1 px-1"
                          style={{ borderBottom: '1px solid #eee' }}>
                          <IconFile size={14} className="text-muted flex-shrink-0" />
                          <span className="flex-fill text-truncate" style={{ fontSize: 13 }}>{f.name}</span>
                          <small className="text-muted flex-shrink-0">{formatSize(f.size)}</small>
                          {f.uploading && (
                            <span className="spinner-border spinner-border-sm text-primary flex-shrink-0"
                              style={{ width: 14, height: 14 }} />
                          )}
                          {f.error && (
                            <span className="badge bg-danger-lt text-danger flex-shrink-0"
                              style={{ fontSize: 10 }}>실패</span>
                          )}
                          {!f.uploading && (
                            <button type="button" className="btn-close flex-shrink-0"
                              style={{ fontSize: 10 }} onClick={() => removeAttach(f.tempId)} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 수정 시 기존 첨부파일 표시 */}
                  {view === 'edit' && selectedPost?.attachments?.length > 0 && (
                    <div className="mt-2 text-muted small">
                      <IconPaperclip size={12} className="me-1" />
                      기존 첨부파일 {selectedPost.attachments.length}개 (새 파일 추가만 가능)
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="card-footer d-flex gap-2">
              <button className="btn btn-secondary btn-sm"
                onClick={() => {
                  setView(selectedPost ? 'detail' : 'list');
                  setAttachFiles([]);
                }}>
                취소
              </button>
              <button className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                onClick={handleSave} disabled={saving || attachFiles.some(f => f.uploading)}>
                {saving ? <span className="spinner-border spinner-border-sm" /> : null}
                {view === 'write' ? '등록' : '수정 완료'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
